import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import { formatUniqueReference, getReference } from '../../../../components/custom-worksheet/utils';
import { CWTRef, CWTSchema, FieldType, WorksheetSchema, WorksheetSchemaField } from '../../../../types/custom-worksheet-v2';
import {
    addFieldToSectionSummary,
    addListItem,
    buildNewAdditionalField,
    buildNewRow,
    buildReferenceMap,
    getField,
    getProperty,
    isField,
    isRef,
    isSection,
    isTable,
    removeFieldFromSectionSummary,
    removeListItem,
    reorder,
} from './utils';
import { get, set, unset } from 'lodash';
import { Calculator } from './calculator';
import traverse from 'traverse';
import { Calculation, Worksheet } from '../../../../types/worksheet';

export type ReferenceMap = { [key: string]: boolean };

export type CWState = {
    dirty: boolean;

    /**
     * Worksheet schema value
     */
    value: WorksheetSchema;
    /**
     * List of reference keys in the template
     */
    referenceMap: ReferenceMap;

    /**
     * Property that's currently being renamed
     */
    renamingPropertyId?: string;
    /**
     * Field with value currently being edited
     */
    currentEditingField?: string;
    /**
     * Field that's focused and will be scrolled into view before playing a flash animation
     */
    currentFocusFieldId?: { id: string; playAnimation: boolean };
    /**
     * Whether every section within schema form should be collapsed
     */
    collapseAll?: boolean;

    calculation: Calculation;
    worksheet?: Worksheet;
    /**
     * Whether to show hidden fields
     */
    showHidden: boolean;

    currentTab?: string;
};

const initialState: CWState = {
    value: { version: '1', properties: {}, reviewConditions: [] },
    referenceMap: {},
    dirty: false,
    calculation: {} as Calculation,
    showHidden: false,
};

// @ts-ignore
const schemaSlice = createSlice({
    name: 'custom-worksheet-ws-schema',
    initialState,
    reducers: {
        reset: (state: CWState) => {
            state.value = { version: '1', properties: {}, reviewConditions: [] };
            state.referenceMap = {};
            state.dirty = false;
            state.calculation = {} as Calculation;
            state.worksheet = undefined;
            state.showHidden = false;
            state.currentEditingField = undefined;
            state.collapseAll = undefined;
            state.currentFocusFieldId = undefined;
            state.renamingPropertyId = undefined;
            state.currentTab = undefined;
        },
        loadSchema: (
            state: CWState,
            action: PayloadAction<{ schema?: WorksheetSchema; calculation?: Calculation; worksheet?: Worksheet }>
        ) => {
            if (!action.payload?.schema) {
                return;
            }
            const { schema } = action.payload;
            state.dirty = false;
            state.value = schema;

            if (action.payload.calculation) {
                state.calculation = action.payload.calculation;
            }

            state.worksheet = action.payload.worksheet;
            // Build initial reference map
            state.referenceMap = buildReferenceMap(state.value);
        },

        markClean: (state: CWState) => {
            state.dirty = false;
        },
        setRenamingPropertyId: (state: CWState, action: PayloadAction<string | undefined>) => {
            state.renamingPropertyId = action.payload;
        },

        setCurrentEditingField: (state: CWState, action: PayloadAction<string | undefined>) => {
            state.currentEditingField = action.payload;
        },

        setCurrentFocusField: (state: CWState, action: PayloadAction<{ id: string; playAnimation: boolean } | undefined>) => {
            state.currentFocusFieldId = action.payload;
        },

        setCurrentTab: (state: CWState, action: PayloadAction<{ id?: string } | undefined>) => {
            state.currentTab = action.payload?.id;
        },

        editField: (
            state: CWState,
            action: PayloadAction<{ id: string; details: Partial<Omit<WorksheetSchemaField, 'type'> & { type: FieldType }> }>
        ) => {
            const field = getField(state.value, action.payload.id);

            if (!field) {
                return;
            }

            let updatedFieldId = field.id;

            // Build updated field object with updated name/type/value
            let updatedField = {
                ...field,
                name: action.payload.details.name !== undefined ? action.payload.details.name : field.name,
                type: action.payload.details.type !== undefined ? action.payload.details.type : field.type,
                value: action.payload.details.value !== undefined ? action.payload.details.value : field.value,
            };

            // When name changes, generate new field id and update reference map
            if (action.payload.details.name) {
                const referenceMap = state.referenceMap;
                const prevReference = getReference(action.payload.id);
                const newReference = formatUniqueReference(action.payload.details.name, referenceMap);

                updatedFieldId = `${updatedFieldId.split('.').slice(0, -1).join('.')}.${newReference}`;
                updatedField = { ...updatedField, id: updatedFieldId };

                // Update current reference map with new reference value
                delete referenceMap[prevReference];
                referenceMap[newReference] = true;
                state.referenceMap = referenceMap;
                state.dirty = true;
            }

            // When either field name or type changes, check if the field belongs to a section summary
            if (action.payload.details.name || action.payload.details.type) {
                const parentPropertyId = action.payload.id.split('.').slice(0, -2).join('.');
                const parentProperty = getProperty(state.value, parentPropertyId);

                if (parentProperty && isSection(parentProperty)) {
                    let sectionSummary = parentProperty.summary?.properties[getReference(parentProperty.id)];

                    if (sectionSummary) {
                        // If field type has changed, add or remove field from section summary
                        if (action.payload.details.type) {
                            if (sectionSummary.type === field.type) {
                                sectionSummary = removeFieldFromSectionSummary(sectionSummary, field.id);
                                updatedField = { ...updatedField, dependentFields: [] };
                            } else if (sectionSummary.type === action.payload.details.type) {
                                sectionSummary = addFieldToSectionSummary(sectionSummary, updatedFieldId);
                                updatedField = {
                                    ...updatedField,
                                    dependentFields: [
                                        { ref: `#${parentProperty.id}.summary.properties.${getReference(parentProperty.id)}` },
                                    ],
                                };
                            }
                        }

                        // If only field name changes, update field reference in section summary calculation
                        else if (action.payload.details.name) {
                            traverse(sectionSummary).forEach(function (s) {
                                if (isRef(s) && s.ref.substring(1) === action.payload.id) {
                                    s.ref = `#${s.ref.substring(1).split('.').slice(0, -1).join('.')}.${getReference(updatedFieldId)}`;
                                }
                                this.update(s);
                            });
                        }

                        set(state.value, `${parentProperty.id}.summary`, {
                            properties: { [getReference(parentProperty.id)]: sectionSummary },
                        });
                    }
                }
                state.dirty = true;
            }

            // Remove property with prev reference from schema
            if (updatedFieldId !== action.payload.id) {
                unset(state.value, action.payload.id);
            }

            // Add property with new reference, id, name, type, value into schema
            set(state.value, updatedFieldId, updatedField);

            if (action.payload.details.value !== undefined && action.payload.details.value !== field.value) {
                if (field.calculation !== null) {
                    state.value = set(state.value, `${action.payload.id}.overridden`, action.payload.details.value !== null);
                }

                // Run calculations
                const value = state.value;
                const calculator = new Calculator(current(value));
                state.value = calculator.run(action.payload.id);

                state.dirty = true;
            }
        },

        editPropertyName: (state: CWState, action: PayloadAction<{ id: string; value: string }>) => {
            state.value = set(state.value, `${action.payload.id}.name`, action.payload.value);
            state.dirty = true;
        },

        setCollapseAll: (state: CWState, action: PayloadAction<boolean | undefined>) => {
            state.collapseAll = action.payload;
        },

        setShowHidden: (state: CWState, action: PayloadAction<boolean>) => {
            state.showHidden = action.payload;
        },

        addInstance: (state: CWState, action: PayloadAction<string>) => {
            // Check if list and template exist
            const list = get(state.value, action.payload);

            if (!list) {
                return;
            }

            if (state.value) {
                const newListItem = addListItem(state.value, list.id);

                // Add dependent fields to other list items
                traverse(newListItem).forEach(function (x) {
                    if (isRef(x)) {
                        const parentFieldNode = this.parents.find((p) => isField(p.node) && p.key !== 'properties')?.node;
                        if (parentFieldNode) {
                            const item = get(state.value, x.ref.substring(1));
                            const index = newListItem.order - 1;
                            if (item && Array.isArray(item.dependentFields)) {
                                const dependencies = item.dependentFields.map((df: CWTRef) => {
                                    let str = df.ref;
                                    str = str.replaceAll('.__prev__', `[${index - 1}]`);
                                    str = str.replaceAll('.__self__', `[${index}]`);
                                    str = str.replaceAll('.__next__', `[${index + 1}]`);
                                    return {
                                        ref: str,
                                    };
                                });

                                set(state.value, `${item.id}.dependentFields`, dependencies);
                            }
                        }
                    }
                });

                // Run initial calculations
                const calculator = new Calculator(current(state.value));
                state.value = calculator.run(newListItem.id);

                // After adding new list item, auto focus on input to allow updating list item name
                state.renamingPropertyId = newListItem.id;
            }
            state.dirty = true;
        },

        removeInstance: (state: CWState, action: PayloadAction<string>) => {
            removeListItem(state.value, action.payload);
            const listId = (action.payload.split('.') || []).slice(0, -1).join('.');

            if (state.value) {
                const calculator = new Calculator(current(state.value));
                state.value = calculator.run(listId);
            }
            state.dirty = true;
        },

        addRow: (state: CWState, action: PayloadAction<string>) => {
            const tableId = action.payload;
            const table = get(state.value, tableId);

            if (!table || !isTable(table)) {
                return;
            }

            if (state.value) {
                const row = buildNewRow(table);

                set(state.value, `${table.id}.properties`, [...table.properties, row]);

                const calculator = new Calculator(current(state.value));
                state.value = calculator.run(row.id);
                state.value = calculator.run(table.id);
            }

            state.dirty = true;
        },

        removeRows: (state: CWState, action: PayloadAction<string[]>) => {
            const rowIds = action.payload;

            if (rowIds.length === 0) {
                return;
            }

            const tableId = (rowIds[0].split('.') || []).slice(0, -1).join('.');
            const table = get(state.value, tableId);

            if (!isTable(table)) {
                return;
            }

            const rows = table.properties;

            const remainingRows = rows.filter((r) => !rowIds.includes(r.id));
            const properties = reorder(tableId, remainingRows);

            set(state.value, `${table.id}.properties`, properties);
            if (state.value) {
                const calculator = new Calculator(current(state.value));
                state.value = calculator.run(table.id);
            }
            state.dirty = true;
        },

        addAdditionalField: (
            state: CWState,
            action: PayloadAction<{ sectionId: string; name: string; type: FieldType; value: string | null | number }>
        ) => {
            const sectionId = action.payload.sectionId;
            const section = get(state.value, sectionId);

            if (!section || !isSection(section)) {
                return;
            }

            const uniqueReference = formatUniqueReference(action.payload.name, state.referenceMap);

            const field = buildNewAdditionalField({
                section,
                name: action.payload.name,
                reference: uniqueReference,
                type: action.payload.type,
                value: action.payload.value,
            });

            const properties = section.properties;
            properties[uniqueReference] = field;

            set(state.value, `${section.id}.properties`, properties);

            // Add new field to section summary if there is a section summary on the same field type
            const sectionSummary = section.summary?.properties[getReference(section.id)];
            if (sectionSummary && sectionSummary.type === field.type) {
                const newSummary = addFieldToSectionSummary(sectionSummary, field.id);
                set(state.value, `${section.id}.summary`, { properties: { [getReference(section.id)]: newSummary } });
            }

            // Add new field reference to current reference map
            state.referenceMap[uniqueReference] = true;
            state.dirty = true;
        },

        removeAdditionalField: (state: CWState, action: PayloadAction<string>) => {
            const field = getField(state.value, action.payload);

            if (!field) {
                return;
            }

            const sectionId = action.payload.split('.').slice(0, -2).join('.');
            const section = get(state.value, sectionId);

            if (!isSection(section)) {
                return;
            }

            const fields = section.properties;
            delete fields[getReference(action.payload)];
            set(state.value, `${section.id}.properties`, fields);

            // Remove field from section summary if field is included in section summary
            const sectionSummary = section.summary?.properties[getReference(section.id)];
            if (sectionSummary && sectionSummary.type === field.type) {
                const newSummary = removeFieldFromSectionSummary(sectionSummary, action.payload);
                set(state.value, `${section.id}.summary`, { properties: { [getReference(section.id)]: newSummary } });
            }

            // Remove field reference from reference map
            const referenceMap = state.referenceMap;
            delete referenceMap[getReference(action.payload)];
            state.referenceMap = referenceMap;
            state.dirty = true;
        },
    },
});

export const {
    loadSchema,
    setRenamingPropertyId,
    setCurrentEditingField,
    setCurrentFocusField,
    editField,
    editPropertyName,
    setCollapseAll,
    addInstance,
    removeInstance,
    addRow,
    removeRows,
    addAdditionalField,
    removeAdditionalField,
    markClean,
    setShowHidden,
    setCurrentTab,
    reset,
} = schemaSlice.actions;

export default schemaSlice.reducer;
